import { Client, getStateCallbacks, type Room } from '@colyseus/sdk';

/**
 * Colyseus client (@colyseus/sdk 0.18 — the client that pairs with the 0.18
 * server and @colyseus/schema 5). Auth is the runway_session cookie: the SDK's
 * matchmaking POST uses credentials:'include', and the server's static onAuth
 * reads the Cookie header, so nothing extra is needed here.
 *
 * URL shape: `${endpoint}/matchmake/:method/:room` (HTTP) then
 * `${endpoint}/<processId>/<roomId>?sessionId=…` (WS). In dev the endpoint is
 * `${origin}/colyseus`, which vite.config.ts proxies to :8000 with the prefix
 * stripped; in production the app is served by the same Node process at the root.
 *
 * The server only exposes joinOrCreate / joinById / reconnect (show rooms are created
 * by POST /api/shows), so those are the only matchmake calls here.
 */
export const COLYSEUS_PATH = import.meta.env.DEV ? '/colyseus' : '';

export function colyseusEndpoint(): string {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}${COLYSEUS_PATH}`;
}

let client: Client | null = null;
export function getColyseusClient(): Client {
  client ??= new Client(colyseusEndpoint());
  return client;
}

/** Everyone joins the single shared 'hub' room (pre-created by the server). */
export function joinHub<State = unknown>(): Promise<Room<unknown, State>> {
  return getColyseusClient().joinOrCreate<State>('hub', {});
}

/** Guests: join an existing show room by its Colyseus roomId (shows.room_id). */
export function joinShowRoom<State = unknown>(roomId: string): Promise<Room<unknown, State>> {
  return getColyseusClient().joinById<State>(roomId, {});
}

/** Reconnect with a token kept from `room.reconnectionToken`. */
export function reconnectRoom<State = unknown>(reconnectionToken: string): Promise<Room<unknown, State>> {
  return getColyseusClient().reconnect<State>(reconnectionToken);
}

export { getStateCallbacks };
export type { Room };
